import { useEffect, useState } from 'react';
import { TAdditionalInfoFormContentProps, TPepEntity } from '../types';
import { PEPDataModal } from '../PEPDataModal/PEPDataModal';
import { defaultPepInfo, PepSection } from '../constants';
import { useSelector } from 'react-redux';
import { useGetDefaultPepInfo } from '../hooks/useGetDefaultPepInfo';
import { errorMessages } from '~/constants/errorMessages.ts';
import { styles } from '~/modules/identification/forms/AdditionalInformation/styles.ts';
import { Box, Grid } from '@mui/material';
import { FormInput } from '~/components/FormComponents/FormInput.tsx';
import { FormCheckbox } from '~/components/FormComponents/FormCheckbox.tsx';
import { useFormContext, useWatch } from 'react-hook-form';
import PepInfoSection, {
  PepInfoSectionProps,
} from '~/modules/identification/forms/AdditionalInformation/AdditionalInfoFormContent/PepInfoSection.tsx';
import { selectFullName } from '~/store/identification-form/selectors';

const AdditionalInfoFormContent = ({ index }: TAdditionalInfoFormContentProps) => {
  const fullName = useSelector(selectFullName);
  const defaultClientPepInfo = useGetDefaultPepInfo(index);
  const [selectedSection, setSelectedSection] = useState<PepSection | null>(null);

  const {
    setValue,
    trigger,
    control,
    formState: { errors },
  } = useFormContext<{ additionalInformation: TPepEntity[] }>();

  const [isPepsAbsent, isPep, isFamilyPep, isPepRelatedPerson] = useWatch({
    control,
    name: [
      `additionalInformation.${index}.isPepsAbsent`,
      `additionalInformation.${index}.isPep`,
      `additionalInformation.${index}.isFamilyPep`,
      `additionalInformation.${index}.isPepRelatedPerson`,
    ],
  });

  const fieldErrors = errors?.additionalInformation?.[index];
  const errorText =
    fieldErrors?.isPepsAbsent?.message === errorMessages.AT_LEAST_ONE_SELECTED
      ? errorMessages.AT_LEAST_ONE_SELECTED
      : (!!fieldErrors?.pepInfo ||
          !!fieldErrors?.familyPepsInfo ||
          !!fieldErrors?.pepRelatedPersonsInfo) &&
        'Перевірте правильність заповнення форми';

  const setFieldValue = (
    field: keyof TPepEntity,
    value: TPepEntity[keyof TPepEntity],
    shouldValidate?: boolean
  ) => {
    setValue(`additionalInformation.${index}.${field}` as const, value, { shouldValidate });
  };

  const isPepDisabled = isPepsAbsent || !isPep;
  const isFamilyPepDisabled = isPepsAbsent || !isFamilyPep;
  const isPepRelationDisabled = isPepsAbsent || !isPepRelatedPerson;

  useEffect(() => {
    if (fullName) {
      setValue(`additionalInformation.${index}.fullName`, fullName);
    }
  }, [fullName, index]);

  const handleFamilyPepChange = (isChecked: boolean) => {
    setFieldValue('isFamilyPep', isChecked, true);
    if (isChecked) {
      setFieldValue(PepSection.familyPepsInfo, [defaultPepInfo]);
      setSelectedSection(PepSection.familyPepsInfo);
    } else {
      setFieldValue(PepSection.familyPepsInfo, null, true);
    }
  };

  const handlePepRelatedPersonChange = (isChecked: boolean) => {
    setFieldValue('isPepRelatedPerson', isChecked, true);
    if (isChecked) {
      setFieldValue(PepSection.pepRelatedPersonsInfo, [defaultPepInfo]);
      setSelectedSection(PepSection.pepRelatedPersonsInfo);
    } else {
      setFieldValue(PepSection.pepRelatedPersonsInfo, null, true);
    }
  };

  const handlePepChange = (isChecked: boolean) => {
    setFieldValue('isPep', isChecked, true);
    if (isChecked) {
      setFieldValue(PepSection.pepInfo, defaultClientPepInfo);
      setSelectedSection(PepSection.pepInfo);
    } else {
      setFieldValue(PepSection.pepInfo, null, true);
    }
  };

  const handleChangeIsPepAbsent = (isChecked: boolean) => {
    setFieldValue('isPepsAbsent', isChecked, true);
    if (isChecked) {
      setFieldValue(PepSection.pepInfo, null);
      setFieldValue(PepSection.pepRelatedPersonsInfo, null);
      setFieldValue(PepSection.familyPepsInfo, null);
      setFieldValue('isPep', false);
      setFieldValue('isPepRelatedPerson', false);
      setFieldValue('isFamilyPep', false);
      setTimeout(() => {
        trigger(`additionalInformation.${index}.pepInfo`);
        trigger(`additionalInformation.${index}.pepRelatedPersonsInfo`);
        trigger(`additionalInformation.${index}.familyPepsInfo`);
        trigger(`additionalInformation.${index}.isPep`);
        trigger(`additionalInformation.${index}.isPepRelatedPerson`);
        trigger(`additionalInformation.${index}.isFamilyPep`);
      }, 100);
    }
  };

  const isPepError = !!fieldErrors?.pepInfo;
  const isFamilyPepError = !!fieldErrors?.familyPepsInfo;
  const isPepRelatedError = !!fieldErrors?.pepRelatedPersonsInfo;

  const pepSectionsProps: Array<PepInfoSectionProps> = [
    {
      name: `additionalInformation.${index}.isPep`,
      onChange: handlePepChange,
      text: 'PEP',
      onClick: () => setSelectedSection(PepSection.pepInfo),
      isCheckboxDisabled: isPepsAbsent,
      isButtonDisabled: isPepDisabled,
      isError: isPepError,
    },
    {
      name: `additionalInformation.${index}.isFamilyPep`,
      onChange: handleFamilyPepChange,
      text: "Член сім'ї РЕР",
      onClick: () => setSelectedSection(PepSection.familyPepsInfo),
      isCheckboxDisabled: isPepsAbsent,
      isButtonDisabled: isFamilyPepDisabled,
      isError: isFamilyPepError,
    },
    {
      name: `additionalInformation.${index}.isPepRelatedPerson`,
      onChange: handlePepRelatedPersonChange,
      text: "Особа, пов'язана з PЕP",
      onClick: () => setSelectedSection(PepSection.pepRelatedPersonsInfo),
      isCheckboxDisabled: isPepsAbsent,
      isButtonDisabled: isPepRelationDisabled,
      isError: isPepRelatedError,
    },
  ];

  return (
    <Grid container sx={styles.wrapper} spacing={1}>
      <Grid size={12}>
        <FormInput
          name={`additionalInformation.${index}.fullName`}
          disabled
          label="ПІБ"
          fullWidth
        />
      </Grid>
      <Grid size={12}>
        <FormCheckbox
          name={`additionalInformation.${index}.isPepsAbsent`}
          label="Не належу до політично значущих осіб, членів їх сімей чи осіб, пов'язаних з політично значущими особами"
          onChange={handleChangeIsPepAbsent}
        />
      </Grid>
      <Grid container sx={styles.additionalInfoModal}>
        <Grid container spacing={{ sm: 0, md: 3 }}>
          {pepSectionsProps.map((section) => (
            <PepInfoSection {...section} key={section.text} />
          ))}
        </Grid>
        <Box sx={styles.textFieldError}>{errorText}</Box>
        <PEPDataModal
          section={selectedSection}
          pepIndex={index}
          onClose={() => setSelectedSection(null)}
        />
      </Grid>
    </Grid>
  );
};

export default AdditionalInfoFormContent;
